// Core lib dependencies.
import { combineReducers, legacy_createStore, applyMiddleware, compose } from 'redux';
import { thunk } from 'redux-thunk';
import Immutable from 'seamless-immutable';

// Common component dependencies.
import OverlayInterface from './common/Overlay/index.js';
import SharingInterface from './common/Sharing/index.js';
import VotingInterface from './common/Voting/index.js';
import NavigationInterface from './common/Navigation/index.js';
import PrivacyConfig from './PrivacyManagement/config/config.js';

// Services.
import ServiceContainer from '../common/services/ServiceContainer.js';
import ConfigService from '../common/services/ConfigService.js';
import StateService from '../common/services/StateService.js';
import AjaxService from '../common/services/AjaxService.js';
import EventService from '../common/services/EventService.js';
import StorageService from '../common/services/StorageService.js';
import PrivacyService from '../common/services/PrivacyService.js';
import ApiService from '../common/services/ApiService.js';

/**
 * <pre>
 * The NGXD (D for Display!) app.
 *
 * Summary of responsibilities:
 *
 *  - acts as a central registry for other Redux/React 'apps'
 *  - allows registering of module config data (output from gsp)
 *  - combines registered Redux reducers into single root reducer
 *  - applies any Redux middleware required
 *  - initialises service container and corresponding services
 *  - provides convenience methods to access the Redux store and services
 *
 * FYI:
 *
 * During init, each registered app will have its corresponding init method called, as a convenience, the Redux store,
 * along with any module config appropriate to that app (eg. social/entries config for Ugc Walls), will be injected as arguments.
 * </pre>
 *
 * @author Tim Loram
 * @module NGXD
 *
 * @type {Object}
 */
const NGXD = {
  // Inits the NGXD namespace.
  init: function (legacyApp) {
    // On page ready.
    legacyApp.App.Events.on('page:ready', function () {
      // Init (inject reference to legacy app so we never have to reference its global namespace).
      NGXD.app.init(legacyApp);
    });
  },

  /**
   * The NGXD app closure.
   *
   * @memberof module:NGXD
   */
  app: (function () {
    'use strict';

    // The Redux store.
    let store = null;

    // Registered Redux apps/reducers.
    const appRegistry = {};
    const reducerRegistry = {
      common: {},
      app:    {},
    };

    // Module configuration.
    const moduleConfigByDisplayType = {};

    // Expose API methods on registered apps only.
    const appApi = {};

    // The service container.
    let _serviceContainer;

    /**
     * Initialises the NGXD app closure.
     *
     * Combines reducers, calls init method of any registered apps, passing Redux store and module config in each case.
     *
     * @memberof module:NGXD
     *
     * @param legacyApp	{object} Reference to the legacy NGX app.
     */
    const init = (legacyApp) => {
      // Create and hydrate service container.
      _serviceContainer = initServices(legacyApp);

      // Register any common reducer dependencies.
      registerReducer(OverlayInterface.Config.stateKey, OverlayInterface.Reducer, true);
      registerReducer(SharingInterface.Config.stateKey, SharingInterface.Reducer, true);
      registerReducer(VotingInterface.Config.stateKey, VotingInterface.Reducer, true);
      registerReducer(NavigationInterface.Config.stateKey, NavigationInterface.Reducer, true);

      // Combine all registered apps & reducers into a single root reducer.
      const rootReducers = {};

      // Combine all common reducers.
      for (const reducerKey in reducerRegistry.common) {
        rootReducers[reducerKey] = reducerRegistry.common[reducerKey];
      }

      // Only combine app reducers if we have at least one module actively using it.
      for (const reducerKey in reducerRegistry.app) {
        if (moduleConfigByDisplayType[reducerKey.toLowerCase()]) {
          rootReducers[reducerKey] = reducerRegistry.app[reducerKey];
        }
      }

      // Always add privacy managment app reducer, since its not dependent on modules.
      rootReducers[PrivacyConfig.stateKey] = reducerRegistry.app[PrivacyConfig.stateKey];

      const rootReducer = combineReducers(rootReducers);

      const hasDevToolsExtension = () => {
        return (
          typeof window === 'object' &&
          window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
          /Chrome/.test(navigator.userAgent) &&
          /Google Inc/.test(navigator.vendor)
        );
      };
      // This is for adding the redux-devtools-extension
      const composeEnhancers =
        process.env.NODE_ENV !== 'production' && hasDevToolsExtension()
          ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
          : compose;
      // Create store, wrap resulting root reducer in redux-thunk middleware, this helps with dispatching async actions.
      store = legacy_createStore(rootReducer, composeEnhancers(applyMiddleware(thunk)));

      // Init all registered apps that have at least one module actively using them.
      for (const reducerKey in appRegistry) {
        if (moduleConfigByDisplayType[reducerKey.toLowerCase()]) {
          const currentApp = appRegistry[reducerKey];

          // Register public API methods for this app.
          if (Object.keys(currentApp.app.api).length) {
            appApi[currentApp.publicName] = currentApp.app.api;
          }

          // Init the app.
          // - inject the store, module config, service container
          currentApp.app.init(store, moduleConfigByDisplayType[reducerKey.toLowerCase()], _serviceContainer);
        }
      }

      // Privacy managment app isn't tied to a module, so always init it.
      appRegistry[PrivacyConfig.stateKey].app.init(store);

      const eventService = getServiceContainer().getService('event');

      /*  Setting up page navigation listeners for each module.  The navigation listeners allow us to keep track of which module
       *   is currently visible on the page so that we don't unnecessarily render the visual.  Rendering a visual on hidden tabs
       *   was causing some resizing issues for that hidden visual due to the visual not having proper dimensions (offsetWidth, offsetHeight).
       *
       *   moduleConfigByDisplayType is an object with a mapping of display type to an object of module configs keyed by module id.
       *   Here we are going to reduce the moduleConfigByDisplayType to one object that maps module id to module.  We then iterate
       *   through each object and assign the event listener based on the module id and the page name (stateName)
       */
      const moduleConfigs = _.reduce(
        _.values(moduleConfigByDisplayType),
        (result, val, key) => {
          return Object.assign(result, val);
        },
        {}
      );

      _.each(moduleConfigs, (mc, id) => {
        eventService.listen('navigate:page', function () {
          store.dispatch(NavigationInterface.Actions.hideModule(id));
        });
        eventService.listen('navigate:page:' + mc.stateName, function () {
          store.dispatch(NavigationInterface.Actions.showModule(id));
        });
      });
    };

    /**
     * Create and hydrate service container.
     *
     * @memberof module:NGXD
     *
     * @param legacyApp	{object} Reference to the legacy NGX app.
     * @return {Common.Services.ServiceContainer} The hydrated service container.
     */
    const initServices = (legacyApp) => {
      // Init the container.
      ServiceContainer.init();

      // Init Config service and add to container.
      // NOTE: config service init() moved to ngx.js for privacy service usage.
      ServiceContainer.addService('config', ConfigService);

      // Init State service and add to container.
      StateService.init(legacyApp.App.state);
      ServiceContainer.addService('state', StateService);

      // Init Ajax service and add to container.
      AjaxService.init(ConfigService);
      ServiceContainer.addService('ajax', AjaxService);

      // Init Event service and add to container.
      EventService.init(legacyApp.App.Events);
      ServiceContainer.addService('event', EventService);

      // Init Storage service and add to container.
      // NOTE: storage service init() moved to ngx.js for privacy service usage.
      ServiceContainer.addService('storage', StorageService);

      // Init Privacy service and add to container.
      // NOTE: privacy service init() moved to ngx.js so we can use it earlier.
      ServiceContainer.addService('privacy', PrivacyService);

      // Init Api service and add to container.
      ApiService.init(legacyApp.App.api);
      ServiceContainer.addService('api', ApiService);

      // Return the hydrated container.
      return ServiceContainer;
    };

    /**
     * Allow a Redux app to be registered.
     *
     * @memberof module:NGXD
     *
     * @param publicName  			{string} Public name to expose the apps api methods.
     * @param reducerKey 			{string} The Redux state key corresponding to this app.
     * @param reducer 				{function} The Redux reducer.
     * @param app 					{function} The app closure, exposing single init method.
     * @param reducerDependencies 	{array} Array of additional reducer dependencies for this app.
     */
    const registerApp = (publicName, reducerKey, reducer, app, reducerDependencies) => {
      // Register app internally.
      appRegistry[reducerKey] = {
        app:        app,
        publicName: publicName,
      };
      registerReducer(reducerKey, reducer);

      // Register additional reducer dependencies.
      for (const reducerDepKey in reducerDependencies) {
        const reducerDependency = reducerDependencies[reducerDepKey];
        registerReducer(reducerDependency.stateKey, reducerDependency.reducer, reducerDependency.common);
      }
    };

    /**
     * Allow just a reducer to be registered (for additional smaller components that do not have their own app, eg. Voting).
     *
     * @memberof module:NGXD
     *
     * @param reducerKey 	{string} The Redux state key for this reducer.
     * @param reducer 		{function} The Redux reducer.
     * @param reducer 		{boolean} Is this a common reducer.
     */
    const registerReducer = (reducerKey, reducer, common = false) => {
      const reducerType = common ? 'common' : 'app';

      // Register, if not already registered.
      if (!reducerRegistry[reducerType][reducerKey]) {
        reducerRegistry[reducerType][reducerKey] = reducer;
      }
    };

    /**
     * Allow module config to be registered.
     *
     * @memberof module:NGXD
     *
     * @param appKey 	{string} The app this module config is relevent too. This should be equal to the Redux state key for a given app.
     * @param id 		{string} The modules DOM id.
     * @param config 	{object} The modules config as an object literal.
     */
    const registerModuleConfig = (appKey, id, config) => {
      const _appKey = appKey.toLowerCase();

      if (!moduleConfigByDisplayType[_appKey]) {
        moduleConfigByDisplayType[_appKey] = {};
      }

      moduleConfigByDisplayType[_appKey][id] = config;
    };

    /**
     * Convenience method to view a human readable version of the Redux state.
     *
     * @memberof module:NGXD
     *
     * @return {object} The State.
     */
    const viewState = () => {
      const humanState = {};
      const state = getStore().getState();

      for (const stateKey in state) {
        // Convert Immutable objects to plain JS objects.
        humanState[stateKey] = Immutable.asMutable(state[stateKey], { deep: true });
      }

      return humanState;
    };

    /**
     * Return the Redux store.
     *
     * @memberof module:NGXD
     *
     * @return {object} The Redux store.
     */
    const getStore = () => {
      return store;
    };

    /**
     * Return the service container.
     *
     * @memberof module:NGXD
     *
     * @return {Common.Services.ServiceContainer} The hydrated service container.
     */
    const getServiceContainer = () => {
      return _serviceContainer;
    };

    // Expose scoped properties.
    return {
      init,

      registerApp,
      registerReducer,
      registerModuleConfig,

      viewState,

      getServiceContainer,
      getStore,

      api: appApi,
    };
  })(),
};

export default NGXD;
